import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { parseMgrsSimple } from "../../utils/fdcHelpers";

function generateCffId() {
  const letters = "ABCDEFGHJKLMNPQRSTUVWXYZ"; // skip I and O for legibility
  const a = letters[Math.floor(Math.random() * letters.length)];
  const b = letters[Math.floor(Math.random() * letters.length)];
  const digits = String(Math.floor(Math.random() * 10000)).padStart(4, "0");
  return `${a}${b}${digits}`;
}

const buildInitialState = () => ({
  cff_id: generateCffId(),
  observer_id: "",
  pct_id: "",
  warning_order: "Adjust Fire",
  method_target_location: "Grid",
  mgrs_target: "",
  altitude: "",
  bearing: "",
  target_number: "",
  target_description: "",
  degree_of_protection: "",
  tiro_vertical: false,
  projectile: "HE",
  fuze: "PD",
  volume_of_fire: "1 round",
  distribution: "Circular",
  method_of_fire: "Battery",
  method_of_control: "At My Command",
  units_to_fire: "",
  changes_to_cff: "",
  angle: "",
  error: "",
  tof: "",
});

function toOptionalNumber(value) {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    fontWeight: 600,
    backgroundColor: "background.default",
  },
  "& .MuiInputLabel-root": {
    fontWeight: 700,
    letterSpacing: 0.2,
  },
  "& .MuiFormHelperText-root": {
    ml: 0.25,
  },
};

const sectionSx = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2,
  p: { xs: 1.25, sm: 1.5 },
  bgcolor: "background.paper",
  boxShadow: 1,
};

function Section({ title, caption, children }) {
  return (
    <Box sx={sectionSx}>
      <Stack spacing={1.25}>
        <Box>
          <Typography sx={{ fontWeight: 600, letterSpacing: 0.2 }}>
            {title}
          </Typography>
          {caption ? (
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
              {caption}
            </Typography>
          ) : null}
        </Box>
        <Divider />
        {children}
      </Stack>
    </Box>
  );
}

export default function CreateFireMissionTool({
  backendUrl,
  defaultMgrsZone = "29SND",
  onCreated,
}) {
  const [form, setForm] = useState(buildInitialState);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const parsedMgrs = useMemo(
    () => parseMgrsSimple(String(form.mgrs_target || "").trim()),
    [form.mgrs_target],
  );

  const mgrsHelperText = parsedMgrs
    ? `Zone ${parsedMgrs.zone} | E ${parsedMgrs.e} | N ${parsedMgrs.n}`
    : `Use zone(5)+10 digits, e.g. ${defaultMgrsZone}6000050000`;

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const resetForm = () => {
    setForm(buildInitialState());
    setError("");
    setSuccess("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    const cffId = String(form.cff_id || "").trim();
    const observerId = String(form.observer_id || "").trim();
    const pctId = String(form.pct_id || "").trim();
    const mgrs = String(form.mgrs_target || "").trim().toUpperCase();
    const parsed = parseMgrsSimple(mgrs);

    if (!cffId || !observerId || !pctId) {
      setError("CFF ID, Observer ID, and PCT ID are required.");
      return;
    }

    if (!parsed) {
      setError(
        "Target MGRS is required and must use zone(5)+10 digits, e.g. 29SND6000050000.",
      );
      return;
    }

    const altitudeNum = toOptionalNumber(form.altitude);
    if (altitudeNum === null) {
      setError("Target altitude is required.");
      return;
    }

    const payload = {
      cff_id: cffId,
      observer_id: observerId,
      pct_id: pctId,
      warning_order: form.warning_order || null,
      method_target_location: form.method_target_location || null,
      estes: parsed.e,
      nortes: parsed.n,
      altitude: altitudeNum,
      bearing: toOptionalNumber(form.bearing),
      target_number: form.target_number || cffId,
      target_description: form.target_description || null,
      degree_of_protection: form.degree_of_protection || null,
      tiro_vertical: Boolean(form.tiro_vertical),
      projectile: form.projectile || null,
      fuze: form.fuze || null,
      volume_of_fire: form.volume_of_fire || null,
      distribution: form.distribution || null,
      method_of_fire: form.method_of_fire || null,
      method_of_control: form.method_of_control || null,
      units_to_fire: form.units_to_fire || null,
      changes_to_cff: form.changes_to_cff || null,
      angle: toOptionalNumber(form.angle),
      error: toOptionalNumber(form.error),
      tof: toOptionalNumber(form.tof),
    };

    try {
      setSubmitting(true);
      const response = await axios.post(`${backendUrl}/submit-data`, payload, {
        timeout: 15000,
      });

      if (response.data?.status !== "success") {
        throw new Error(response.data?.message || "Failed to create fire mission.");
      }

      if (typeof onCreated === "function") {
        await onCreated({ missionId: cffId, payload, parsedMgrs: parsed });
        return;
      }

      setSuccess(`Fire mission ${cffId} created successfully.`);
      setForm((prev) => ({
        ...buildInitialState(),
        observer_id: prev.observer_id,
        pct_id: prev.pct_id,
      }));
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to create fire mission.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={2}>
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            px: 1.5,
            py: 1.25,
            bgcolor: "background.paper",
          }}
        >
          <Typography sx={{ fontWeight: 600, letterSpacing: 0.2 }}>
            Mission Initialization
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            Create a new fire mission record using the same backend flow as ATAK mission creation.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ borderRadius: 2, fontWeight: 700 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ borderRadius: 2, fontWeight: 700 }}>
            {success}
          </Alert>
        )}

        <Section title="Mission Identity" caption="Core identifiers required to create the mission.">
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                size="small"
                label="CFF ID / Mission ID"
                name="cff_id"
                value={form.cff_id}
                onChange={handleChange}
                helperText="Auto-generated — click 🎲 to regenerate"
                InputProps={{
                  endAdornment: (
                    <Button
                      size="small"
                      onClick={() => setForm((prev) => ({ ...prev, cff_id: generateCffId() }))}
                      sx={{ minWidth: 0, px: 0.6, fontSize: 16, fontWeight: 700 }}
                      tabIndex={-1}
                      title="Generate new CFF ID"
                    >
                      🎲
                    </Button>
                  ),
                }}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                size="small"
                label="Observer ID"
                name="observer_id"
                value={form.observer_id}
                onChange={handleChange}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                size="small"
                label="PCT ID"
                name="pct_id"
                value={form.pct_id}
                onChange={handleChange}
                sx={fieldSx}
              />
            </Grid>
          </Grid>
        </Section>

        <Section title="Target Location" caption="Reference method, location, and vertical data.">
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                size="small"
                label="Warning Order"
                name="warning_order"
                value={form.warning_order}
                onChange={handleChange}
                sx={fieldSx}
              >
                {[
                  "Adjust Fire",
                  "Fire For Effect",
                  "Immediate Suppression",
                  "Immediate Smoke",
                  "Immediate Illumination",
                ].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                size="small"
                label="Method of Target Location"
                name="method_target_location"
                value={form.method_target_location}
                onChange={handleChange}
                sx={fieldSx}
              >
                {["Grid", "Polar", "Shift From Known Point"].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Bearing"
                name="bearing"
                type="number"
                value={form.bearing}
                onChange={handleChange}
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                required
                size="small"
                label="Target MGRS"
                name="mgrs_target"
                value={form.mgrs_target}
                onChange={handleChange}
                helperText={mgrsHelperText}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                size="small"
                label="Target Altitude (m)"
                name="altitude"
                type="number"
                value={form.altitude}
                onChange={handleChange}
                helperText="Required for ballistic calculation"
                sx={fieldSx}
              />
            </Grid>
          </Grid>
        </Section>

        <Section title="Target Description" caption="How the target is described to the battery.">
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Target Number"
                name="target_number"
                value={form.target_number}
                onChange={handleChange}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                label="Target Description"
                name="target_description"
                value={form.target_description}
                onChange={handleChange}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Degree of Protection"
                name="degree_of_protection"
                value={form.degree_of_protection}
                onChange={handleChange}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.tiro_vertical}
                    onChange={handleChange}
                    name="tiro_vertical"
                  />
                }
                label="Vertical Shot"
                sx={{ ml: 0.25, "& .MuiFormControlLabel-label": { fontWeight: 700 } }}
              />
            </Grid>
          </Grid>
        </Section>

        <Section title="Fire Command" caption="Ammunition, distribution, and control settings.">
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Projectile" name="projectile" value={form.projectile} onChange={handleChange} sx={fieldSx} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Fuze" name="fuze" value={form.fuze} onChange={handleChange} sx={fieldSx} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Volume of Fire" name="volume_of_fire" value={form.volume_of_fire} onChange={handleChange} sx={fieldSx} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Distribution" name="distribution" value={form.distribution} onChange={handleChange} sx={fieldSx} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Method of Fire" name="method_of_fire" value={form.method_of_fire} onChange={handleChange} sx={fieldSx} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Method of Control" name="method_of_control" value={form.method_of_control} onChange={handleChange} sx={fieldSx} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Units to Fire" name="units_to_fire" value={form.units_to_fire} onChange={handleChange} sx={fieldSx} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Changes to CFF" name="changes_to_cff" value={form.changes_to_cff} onChange={handleChange} sx={fieldSx} />
            </Grid>
          </Grid>
        </Section>

        <Section title="Ballistic Metadata" caption="Optional calculation values stored with the mission.">
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Angle" name="angle" type="number" value={form.angle} onChange={handleChange} sx={fieldSx} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Error" name="error" type="number" value={form.error} onChange={handleChange} sx={fieldSx} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="TOF" name="tof" type="number" value={form.tof} onChange={handleChange} sx={fieldSx} />
            </Grid>
          </Grid>
        </Section>

        <Stack direction="row" spacing={1} justifyContent="flex-end" useFlexGap flexWrap="wrap">
          <Button
            type="button"
            variant="outlined"
            onClick={resetForm}
            disabled={submitting}
            sx={{ borderRadius: 999, textTransform: "none", fontWeight: 600, minWidth: 120 }}
          >
            Reset
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            sx={{ borderRadius: 999, textTransform: "none", fontWeight: 600, minWidth: 210 }}
          >
            {submitting ? "Creating..." : "Create Fire Mission"}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
